// Shared helpers for the parties / finance / administration domain.
import { adminApi, SelectOption } from '../../api/client';
import { isSuperUser } from '../../auth/permissions';
import { VendiaRuntimeUser } from '../../runtime';
import { CrudRecord } from '../../crud/types';

// --- Money / journal ---------------------------------------------------------

// Journal balances and journal transaction debit/credit are stored in minor
// units (cents). The balance arrives as a Money object { amount, currency }.
export function centsToMajor(value: unknown): number | null {
  if (value == null || value === '') return null;
  if (typeof value === 'object') {
    const amount = (value as { amount?: unknown }).amount;
    return centsToMajor(amount);
  }
  const number = Number(value);
  return Number.isFinite(number) ? number / 100 : null;
}

export function formatAmount(value: number | null | undefined) {
  if (value == null || !Number.isFinite(value)) return '-';
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Journal balance (major units) of a customer/supplier/account row. */
export function journalBalance(row: CrudRecord | Record<string, unknown>): number | null {
  const journal = row.journal as { balance?: unknown } | null | undefined;
  if (!journal) return null;
  return centsToMajor(journal.balance);
}

export function journalBalanceText(row: CrudRecord | Record<string, unknown>) {
  return formatAmount(journalBalance(row));
}

// --- Options adapter ---------------------------------------------------------

// Legacy option endpoints come in several shapes:
//   [{value,label}] · [{id,name}] · { timezones: ['Africa/…'] } ·
//   { currencies: [{code,name}] } · { types: [{code,label}] }
export function adaptOptions(data: unknown): SelectOption[] {
  let list: unknown = data;
  if (list && typeof list === 'object' && !Array.isArray(list)) {
    const values = Object.values(list as Record<string, unknown>);
    const firstArray = values.find(Array.isArray);
    list = firstArray ?? values;
  }
  if (!Array.isArray(list)) return [];
  return list.flatMap((item): SelectOption[] => {
    if (item == null) return [];
    if (typeof item === 'string' || typeof item === 'number') return [{ value: item, label: String(item) }];
    if (typeof item !== 'object') return [];
    const record = item as Record<string, unknown>;
    const value = record.value ?? record.id ?? record.code;
    if (value == null) return [];
    const label = String(record.label ?? record.name ?? record.title ?? record.code ?? value);
    const children = Array.isArray(record.children) ? adaptOptions(record.children) : [];
    return [{ value: value as string | number, label }, ...children];
  });
}

const optionCache = new Map<string, Promise<SelectOption[]>>();
export function fetchAdaptedOptions(endpoint: string): Promise<SelectOption[]> {
  if (!optionCache.has(endpoint)) {
    const promise = adminApi
      .get(endpoint)
      .then((response) => adaptOptions(response.data))
      .catch((err) => {
        optionCache.delete(endpoint);
        throw err;
      });
    optionCache.set(endpoint, promise);
  }
  return optionCache.get(endpoint)!;
}

// --- Relations ---------------------------------------------------------------

export function idList(value: unknown): Array<string | number> {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (entry == null || entry === '') return [];
    if (typeof entry === 'object') {
      const record = entry as Record<string, unknown>;
      const id = record.id ?? record.value;
      return id == null ? [] : [id as string | number];
    }
    return [entry as string | number];
  });
}

export function joinNames(value: unknown, fallback = '-') {
  if (!Array.isArray(value)) return fallback;
  const labels = value
    .map((entry) => (entry && typeof entry === 'object' ? String((entry as Record<string, unknown>).name ?? (entry as Record<string, unknown>).title ?? '') : String(entry ?? '')))
    .filter(Boolean);
  return labels.length ? labels.join(', ') : fallback;
}

// --- Current user (set by form widgets that can use hooks) -------------------

let currentUser: VendiaRuntimeUser | null = null;
export function rememberCurrentUser(user: VendiaRuntimeUser | null) {
  currentUser = user;
}
export function currentUserIsSuper() {
  return isSuperUser(currentUser);
}
export function hasCurrentUser() {
  return currentUser != null;
}

export function asRuntimeUser(value: unknown): VendiaRuntimeUser | null {
  return value && typeof value === 'object' ? (value as VendiaRuntimeUser) : null;
}

// --- Misc --------------------------------------------------------------------

export function flag(value: unknown) {
  return value === true || value === 1 || value === '1' ? 1 : 0;
}

export function nullableNumber(value: unknown) {
  if (value == null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function dateOnly(value: unknown) {
  if (value == null || value === '') return null;
  return String(value).slice(0, 10);
}
